import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useFavorites } from '@/hooks/useFavorites';
import { useTourDiscounts, resolveTourDiscount, applyTourDiscount } from '@/hooks/useTourDiscounts';
import SelectChevron from '@/components/base/SelectChevron';

interface PricingOption {
  label: string;
  price: number;
  description: string;
  info?: string;
}

interface PricingSidebarProps {
  basePrice: number;
  currency: string;
  options: PricingOption[];
  tourId: string;
  tourTitle?: string;
}

export default function PricingSidebar({ basePrice, currency, options, tourId, tourTitle }: PricingSidebarProps) {
  const { isFavorite, toggleFavorite } = useFavorites();
  const { t } = useTranslation();
  const { discounts } = useTourDiscounts();
  const [selectedOption, setSelectedOption] = useState(0);
  const [adults, setAdults] = useState(1);
  const [children, setChildren] = useState(0);
  const [infoOpen, setInfoOpen] = useState<number | null>(null);

  const discount = resolveTourDiscount(tourId, discounts);
  const pricedOptions = options.map((opt) => ({
    ...opt,
    ...applyTourDiscount(opt.price, discount),
  }));
  const currentPrice = pricedOptions[selectedOption]?.finalPrice ?? basePrice;
  const currentOriginal = pricedOptions[selectedOption]?.originalPrice ?? basePrice;
  const total = currentPrice * adults;

  return (
    <div className="bg-white rounded-xl border border-background-200 overflow-hidden lg:overflow-y-auto lg:max-h-[calc(100vh-140px)] shadow-[0_1px_2px_rgba(19,48,44,0.05)] [scrollbar-width:thin] [scrollbar-color:#CBD5E1_transparent] [&::-webkit-scrollbar]:w-[6px] [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-secondary-200/70">
      {/* Header */}
      <div className="p-5 border-b border-background-200/70">
        <p className="text-[11px] font-semibold uppercase tracking-wider text-secondary-400">{t('td.pricing.from')}</p>
        <div className="flex items-baseline gap-1.5 mt-1">
          {pricedOptions[selectedOption]?.hasDiscount && (
            <span className="text-base font-semibold text-secondary-400 line-through">
              {currentOriginal} {currency}
            </span>
          )}
          <span className="text-[26px] font-extrabold text-foreground-950">{currentPrice} {currency}</span>
          <span className="text-xs text-secondary-400">{t('td.pricing.perPerson', { currency })}</span>
        </div>
        <p className="text-xs text-secondary-400 mt-1">{options[selectedOption]?.description || `This journey, 1 adult, ${options[selectedOption]?.label || '3-Star'}`}</p>
      </div>

      {/* Options */}
      <div className="p-5 space-y-2">
        {options.map((opt, idx) => (
          <div
            key={opt.label}
            onClick={() => setSelectedOption(idx)}
            className={`relative w-full flex items-center justify-between px-3.5 py-2.5 rounded-lg border text-left transition-all cursor-pointer ${
              selectedOption === idx
                ? 'border-primary-500 bg-primary-50'
                : 'border-background-200 hover:border-background-300'
            }`}
          >
            <div className="flex items-center gap-2.5">
              <span className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                selectedOption === idx ? 'border-primary-500' : 'border-secondary-300'
              }`}>
                {selectedOption === idx && <span className="w-2 h-2 rounded-full bg-primary-500" />}
              </span>
              <span className="text-sm font-medium text-foreground-950 flex items-center gap-1.5">
                {opt.label}
                {opt.info && (
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); setInfoOpen(infoOpen === idx ? null : idx); }}
                    className="w-4 h-4 rounded-full bg-secondary-200 hover:bg-primary-500 hover:text-white flex items-center justify-center text-secondary-600 transition-colors cursor-pointer"
                    aria-label={`More about ${opt.label}`}
                  >
                    <i className="ri-information-line text-[11px] leading-none" />
                  </button>
                )}
              </span>
            </div>
            <span className="flex items-baseline gap-1.5 text-sm font-semibold text-foreground-950">
              {pricedOptions[idx].hasDiscount && (
                <span className="text-xs font-normal text-secondary-400 line-through">
                  ~{pricedOptions[idx].originalPrice} {currency}
                </span>
              )}
              ~{pricedOptions[idx].finalPrice} {currency}
            </span>

            {infoOpen === idx && opt.info && (
              <div className="absolute left-0 right-0 top-full mt-2.5 z-50">
                <div className="absolute -top-1.5 left-1/2 -translate-x-1/2 w-3 h-3 rotate-45 bg-white border-l border-t border-secondary-200" />
                <div className="relative bg-white border border-secondary-200 rounded-lg px-3.5 py-3 text-xs leading-relaxed text-secondary-800 shadow-[0_12px_32px_rgba(2,6,23,0.16)]">
                  {opt.info}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Travel Budget Planner */}
      <div className="px-5 pb-5">
        <h3 className="text-xs font-semibold text-foreground-950 mb-3">{t('td.pricing.planner')}</h3>
        <div className="mb-3">
          <label className="text-[11px] text-secondary-400 mb-1 block">{t('td.pricing.yourOption')}</label>
          <div className="relative">
            <select
              className="w-full appearance-none bg-white border border-background-200 rounded-lg px-3 py-2.5 text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-300 cursor-pointer"
              value={selectedOption}
              onChange={(e) => setSelectedOption(Number(e.target.value))}
            >
              {options.map((opt, idx) => (
                <option key={opt.label} value={idx}>{opt.label}</option>
              ))}
            </select>
            <SelectChevron className="absolute right-3 top-1/2 -translate-y-1/2 text-secondary-400" />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3 mb-4">
          <div>
            <label className="text-[11px] text-secondary-400 mb-1 block">{t('td.pricing.adults')}</label>
            <div className="flex items-center border border-background-200 rounded-lg">
              <button
                onClick={() => setAdults(Math.max(1, adults - 1))}
                className="w-9 h-9 flex items-center justify-center text-secondary-500 hover:text-foreground-950 cursor-pointer"
                aria-label={t('td.pricing.decreaseAdult')}
              >
                <i className="ri-subtract-line" />
              </button>
              <span className="flex-1 text-center text-sm font-semibold text-foreground-950">{String(adults).padStart(2, '0')}</span>
              <button
                onClick={() => setAdults(adults + 1)}
                className="w-9 h-9 flex items-center justify-center text-secondary-500 hover:text-foreground-950 cursor-pointer"
                aria-label={t('td.pricing.increaseAdult')}
              >
                <i className="ri-add-line" />
              </button>
            </div>
          </div>
          <div>
            <label className="text-[11px] text-secondary-400 mb-1 block">{t('td.pricing.children')}</label>
            <div className="flex items-center border border-background-200 rounded-lg">
              <button
                onClick={() => setChildren(Math.max(0, children - 1))}
                className="w-9 h-9 flex items-center justify-center text-secondary-500 hover:text-foreground-950 cursor-pointer"
                aria-label={t('td.pricing.decreaseChild')}
              >
                <i className="ri-subtract-line" />
              </button>
              <span className="flex-1 text-center text-sm font-semibold text-foreground-950">{String(children).padStart(2, '0')}</span>
              <button
                onClick={() => setChildren(children + 1)}
                className="w-9 h-9 flex items-center justify-center text-secondary-500 hover:text-foreground-950 cursor-pointer"
                aria-label={t('td.pricing.increaseChild')}
              >
                <i className="ri-add-line" />
              </button>
            </div>
          </div>
        </div>

        {/* Total */}
        <div className="flex items-center justify-between bg-secondary-50 rounded-lg px-4 py-3">
          <span className="text-sm text-secondary-500">{t('td.pricing.roughTotal')}</span>
          <span className="text-lg font-bold text-foreground-950">{total} {currency}</span>
        </div>
      </div>

      {/* Important notice */}
      <div className="mx-5 mb-5 bg-amber-50 rounded-lg p-4">
        <div className="flex items-start gap-2">
          <i className="ri-error-warning-line text-amber-500 text-sm mt-0.5 shrink-0" />
          <div>
            <p className="text-xs font-semibold text-amber-700 mb-1">{t('td.pricing.noticeTitle')}</p>
            <ul className="text-[11px] text-amber-600 space-y-1 list-none">
              <li>• <b>{t('td.pricing.notice1a')}</b>{t('td.pricing.notice1b')}</li>
              <li>• <b>{t('td.pricing.notice2a')}</b>{t('td.pricing.notice2b')}</li>
              <li>• {t('td.pricing.notice3')}</li>
              <li>• {t('td.pricing.notice4')}</li>
            </ul>
          </div>
        </div>
      </div>

      {/* CTA row — CTA + heart on same line, matching original */}
      <div className="px-5 pb-5 flex items-center gap-2">
        <Link
          to={`/booking/${tourId}?option=${encodeURIComponent(options[selectedOption]?.label || '')}&adults=${adults}&children=${children}&price=${currentOriginal}`}
          className="flex-1 flex items-center justify-center bg-primary-500 hover:bg-primary-600 text-white text-sm font-semibold py-3 rounded-[10px] transition-colors whitespace-nowrap"
        >
          {t('td.pricing.bookingNow')}
        </Link>
        <button
          onClick={() => toggleFavorite(tourId)}
          aria-label={isFavorite(tourId) ? t('td.pricing.removeSaved') : t('td.pricing.saveTour')}
          aria-pressed={isFavorite(tourId)}
          className="w-11 h-11 border border-background-200 rounded-full flex items-center justify-center transition-colors cursor-pointer hover:border-red-200 shrink-0"
        >
          <i className={isFavorite(tourId) ? 'ri-heart-3-fill text-red-500' : 'ri-heart-3-line text-secondary-400 hover:text-red-500'} />
        </button>
      </div>

      {/* Trust */}
      <div className="px-5 pb-5">
        <div className="flex items-start gap-2 text-[11px] text-secondary-400">
          <i className="ri-shield-check-line text-primary-500 shrink-0 mt-0.5" />
          <div>
            <p>{t('td.pricing.noCharge')}</p>
            <p>Licence No. 01-2641/2024/CDLQGVN-GP LHQT</p>
          </div>
        </div>
      </div>

      {/* Cancellation */}
      <div className="px-5 pb-5 border-t border-background-200/70 pt-4">
        <p className="text-xs font-semibold text-foreground-950 mb-3">{t('td.pricing.cancellation')}</p>

        <div className="mb-3">
          <p className="text-[11px] font-semibold text-secondary-500 mb-1">{t('td.pricing.flightTitle')}</p>
          <ul className="text-[11px] text-secondary-400 space-y-0.5 list-none">
            <li>• {t('td.pricing.flight1')}</li>
            <li>• {t('td.pricing.flight2')}</li>
            <li>• {t('td.pricing.flight3')}</li>
            <li>• {t('td.pricing.flight4')}</li>
          </ul>
        </div>

        <div className="mb-3">
          <p className="text-[11px] font-semibold text-secondary-500 mb-1">{t('td.pricing.groupTitle')}</p>
          <ul className="text-[11px] text-secondary-400 space-y-0.5 list-none">
            <li>• <b>{t('td.pricing.group1a')}</b>{t('td.pricing.group1b')}</li>
            <li>• <b>{t('td.pricing.group2a')}</b>{t('td.pricing.group2b')}</li>
            <li>• <b>{t('td.pricing.group3a')}</b>{t('td.pricing.group3b')}</li>
          </ul>
        </div>

        <div>
          <p className="text-[11px] font-semibold text-secondary-500 mb-1">{t('td.pricing.privateTitle')}</p>
          <ul className="text-[11px] text-secondary-400 space-y-0.5 list-none">
            <li>• <b>{t('td.pricing.private1a')}</b>{t('td.pricing.private1b')}</li>
            <li>• <b>{t('td.pricing.private2a')}</b>{t('td.pricing.private2b')}</li>
            <li>• <b>{t('td.pricing.private3a')}</b>{t('td.pricing.private3b')}</li>
          </ul>
        </div>
      </div>

      {/* Mobile bottom bar */}
      <div className="lg:hidden fixed bottom-0 left-0 right-0 z-[100] bg-white border-t border-background-200 px-4 py-3 flex items-center justify-between">
        <div>
          <span className="text-[16px] font-bold text-foreground-950">{currentPrice} {currency}</span>
          <span className="text-[11px] text-secondary-400 ml-1">{t('td.pricing.mobileJourney', { label: options[selectedOption]?.label || '3-Star' })}</span>
        </div>
        <Link
          to={`/booking/${tourId}?option=${encodeURIComponent(options[selectedOption]?.label || '')}&adults=${adults}&children=${children}&price=${currentOriginal}`}
          className="bg-primary-500 hover:bg-primary-600 text-white text-sm font-semibold px-5 py-2.5 rounded-full transition-colors whitespace-nowrap"
        >
          {t('td.pricing.bookingNow')}
        </Link>
      </div>
    </div>
  );
}